import { type TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { provide } from '@lit/context';
import { TailwindElement } from '../../shared/tailwind.element';
import { type QuestionaireData, questionaireContext } from '../../context/context';
import { apiService } from '../../services/apiService';
import { type QuestionOpenData, type AnswerData, type QuestionaireStartPageData } from '../../interfaces/interfaces';
import { addClarificationAttributes } from '../../shared/clarificationUtil';
import { CustomEventNames } from '../../constants/custom-event-names';
import { ClarificationClass, LocalStorageKey, ResumeUrlParam } from '../../constants/constants';

import '../progress-bar/progress-bar';
import '../question/question';
import '../action-bar/action-bar';
import '../report/report';
import '../dialogs/dialog';
import '../dialogs/clarification-dialog';
import '../dialogs/resume-dialog';
import '../dialogs/error-dialog';

@customElement('szw-ba-layout')
export class SzwBaLayout extends TailwindElement() {
	@provide({ context: questionaireContext })
	@property({ attribute: false })
	data!: QuestionaireData;

	@property({ type: Boolean, attribute: false })
	isLoading = true;

	private clarificationClickHandler?: (event: Event) => void;

	@property({ attribute: false })
	startPageData?: QuestionaireStartPageData;

	async connectedCallback(): Promise<void> {
		super.connectedCallback();
		this.isLoading = true;
		await this.updateComplete;

		// get responseCode from localstorage
		const responseCode = localStorage.getItem(LocalStorageKey);
		if (responseCode != null) {
			// check if autoresume is enabled in querystring
			const urlParams = new URLSearchParams(window.location.search);
			const autoResume = urlParams.get(ResumeUrlParam);
			if (autoResume != null) {
				const questionaireResponse = await apiService.openQuestionnaire(responseCode);
				this.data = questionaireResponse;
				this.isLoading = false;
			} else {
				window.dispatchEvent(new CustomEvent(CustomEventNames.OpenResumeDialog, { bubbles: true, composed: true }));
				await new Promise((resolve) => {
					this.addEventListener(CustomEventNames.DialogCloseOrAccept, (event) => {
						const action = (event as CustomEvent).detail.action;
						if (action === 'continue') {
							const url = new URL(window.location.href);
							url.searchParams.set(ResumeUrlParam, 'true');
							window.history.pushState({}, '', url.toString());
							window.location.reload();
						} else {
							localStorage.removeItem(LocalStorageKey);
						}
						resolve((event as CustomEvent).detail);
					});
				});
				await this.initializeStartPage();
			}
		} else {
			await this.initializeStartPage();
		}

		this.clarificationClickHandler = addClarificationAttributes(this.shadowRoot, this);

		this.addEventListener(CustomEventNames.StartQuestionnaire, this.handleStartQuestionnaire.bind(this));
		this.addEventListener(CustomEventNames.AnswerQuestion, (event) => {
			this.handleAnswerQuestion(event as CustomEvent<AnswerData>);
		});
		this.addEventListener(CustomEventNames.OpenQuestion, (event) => {
			this.handleQuestionOpen(event as CustomEvent<QuestionOpenData>);
		});

		this.addEventListener(CustomEventNames.OpenReport, () => {
			this.handleOpenReport();
		});
	}

	handleOpenReport(): void {
		this.data = { ...this.data, question: null, mustOpenReport: true };
	}

	async initializeStartPage(): Promise<void> {
		this.isLoading = true;
		const startPageResponse = await apiService.getQuestionnaire();
		this.startPageData = startPageResponse;
		this.isLoading = false;
	}

	handleQuestionOpen(event: CustomEvent<QuestionOpenData>): void {
		this.questionOpen(event.detail)
			.catch(() => {})
			.finally(() => {
				this.dispatchEvent(new Event(CustomEventNames.LoadingDone, { bubbles: true, composed: true }));
			});
	}

	handleStartQuestionnaire(_: Event): void {
		this.startQuestionnaire()
			.catch(() => {})
			.finally(() => {
				this.dispatchEvent(new Event(CustomEventNames.LoadingDone, { bubbles: true, composed: true }));
			});
	}

	handleAnswerQuestion(event: CustomEvent<AnswerData>): void {
		this.answerQuestion(event.detail)
			.catch(() => {})
			.finally(() => {
				this.dispatchEvent(new Event(CustomEventNames.LoadingDone, { bubbles: true, composed: true }));
			});
	}

	async questionOpen(questionToOpen: QuestionOpenData): Promise<void> {
		const openQuestionResponse = await apiService.openQuestion(questionToOpen);
		this.data = openQuestionResponse;
	}

	async answerQuestion(answer: AnswerData): Promise<void> {
		const answerQuestionResponse = await apiService.postAnswerQuestion(answer);
		this.data = { ...this.data, ...answerQuestionResponse };
		localStorage.setItem(LocalStorageKey, this.data.responseCode);
	}

	async startQuestionnaire(): Promise<void> {
		const startQuestionaireResponse = await apiService.postStartQuestionnaire();
		this.startPageData = undefined;
		this.data = startQuestionaireResponse;
		localStorage.setItem(LocalStorageKey, startQuestionaireResponse.responseCode);
	}

	startPageTemplate(): TemplateResult {
		return html`<article class="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6">
				${unsafeHTML(this.startPageData?.introHtml)}
			</article>
			<szw-ba-action-bar></szw-ba-action-bar>`;
	}

	render(): TemplateResult {
		return html`${this.isLoading
				? html`<div role="status" class="mx-auto w-full max-w-3xl animate-pulse px-6">
						<div class="mb-6 flex gap-6">
							<div class="h-4 w-full rounded-full bg-donkerblauw-15"></div>
							<div class="h-4 w-8 shrink-0 rounded-full bg-donkerblauw-75"></div>
						</div>
						<div class="mb-8 h-6 max-w-xs rounded-full bg-grijs-6"></div>
						<div class="mb-6 h-4 max-w-md rounded-full bg-grijs-6"></div>

						<div class="mb-6 flex flex-col gap-2">
							<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							<div class="h-4 max-w-sm rounded-full bg-grijs-2"></div>
						</div>

						<div class="mb-6 flex flex-col gap-2">
							<div class="flex gap-4">
								<div class="h-4 w-4 rounded-full bg-donkerblauw-15"></div>
								<div class="h-4 w-full max-w-[20ch] rounded-full bg-grijs-2"></div>
							</div>
							<div class="flex gap-4">
								<div class="h-4 w-4 rounded-full bg-donkerblauw-15"></div>
								<div class="h-4 w-full max-w-[30ch] rounded-full bg-grijs-2"></div>
							</div>
							<div class="flex gap-4">
								<div class="h-4 w-4 rounded-full bg-donkerblauw-15"></div>
								<div class="h-4 w-full max-w-[25ch] rounded-full bg-grijs-2"></div>
							</div>
							<div class="flex gap-4">
								<div class="h-4 w-4 rounded-full bg-donkerblauw-15"></div>
								<div class="h-4 w-full max-w-[50ch] rounded-full bg-grijs-2"></div>
							</div>
						</div>
				  </div>`
				: null}
			${this.startPageData != null ? this.startPageTemplate() : ''}
			<szw-ba-progress-bar></szw-ba-progress-bar>
			${this.data?.question != null && !this.data?.mustOpenReport
				? html`
						<szw-ba-question>
							<szw-ba-action-bar></szw-ba-action-bar>
						</szw-ba-question>
				  `
				: null}
			${this.data?.mustOpenReport
				? html`
						<szw-ba-report>
							<szw-ba-action-bar></szw-ba-action-bar>
						</szw-ba-report>
				  `
				: null}
			<szw-ba-dialog></szw-ba-dialog>
			<szw-ba-clarification-dialog></szw-ba-clarification-dialog>
			<szw-ba-resume-dialog></szw-ba-resume-dialog>
			<szw-ba-error-dialog></szw-ba-error-dialog>`;
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.removeEventListener(CustomEventNames.StartQuestionnaire, this.handleStartQuestionnaire.bind(this));
		this.removeEventListener(CustomEventNames.AnswerQuestion, (event) => {
			this.handleAnswerQuestion(event as CustomEvent<AnswerData>);
		});
		this.removeEventListener(CustomEventNames.OpenQuestion, (event) => {
			this.handleQuestionOpen(event as CustomEvent<QuestionOpenData>);
		});
		if (this.clarificationClickHandler != null) {
			this.shadowRoot?.querySelectorAll(ClarificationClass).forEach((item) => {
				if (this.clarificationClickHandler != null) {
					item.removeEventListener('click', this.clarificationClickHandler);
				}
			});
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'szw-ba-layout': SzwBaLayout;
	}
}
